import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, ArrowLeft } from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { messageHandler } from '../services/message-handler.service';
import { config } from '../lib/config';
import { Form, Button } from '../components/ui';

function TwoFactorVerify() {
  const navigate = useNavigate();
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState<string | null>(null);
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);
  const { twoFactorToken, requiresTwoFactor, isLoading, completeTwoFactorLogin, clearTwoFactor } =
    useAuthStore(state => ({
      twoFactorToken: state.twoFactorToken,
      requiresTwoFactor: state.requiresTwoFactor,
      isLoading: state.isLoading,
      completeTwoFactorLogin: state.completeTwoFactorLogin,
      clearTwoFactor: state.clearTwoFactor,
    }));

  useEffect(() => {
    if (!requiresTwoFactor || !twoFactorToken) {
      navigate('/login', { replace: true });
    } else {
      inputsRef.current[0]?.focus();
    }
  }, [requiresTwoFactor, twoFactorToken, navigate]);

  const handleChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError(null);

    if (value && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length > 0) {
      const newCode = ['', '', '', '', '', ''];
      for (let i = 0; i < pasted.length; i++) {
        newCode[i] = pasted[i];
      }
      setCode(newCode);
      inputsRef.current[Math.min(pasted.length, 5)]?.focus();
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const fullCode = code.join('');
    if (fullCode.length !== 6) {
      setError('Digite os 6 dígitos do código');
      return;
    }

    if (!twoFactorToken) {
      messageHandler.error('Token 2FA expirado. Faça login novamente.');
      clearTwoFactor();
      navigate('/login', { replace: true });
      return;
    }

    try {
      await completeTwoFactorLogin(twoFactorToken, fullCode);
      navigate('/', { replace: true });
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message || 'Código 2FA inválido';
      if (msg.toLowerCase().includes('expirado') || msg.toLowerCase().includes('invalid')) {
        messageHandler.error('Sua sessão 2FA expirou, faça login novamente.');
        clearTwoFactor();
        navigate('/login', { replace: true });
      } else {
        setError(msg);
        setCode(['', '', '', '', '', '']);
        inputsRef.current[0]?.focus();
      }
    }
  };

  const handleBack = () => {
    clearTwoFactor();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-gray-50 dark:bg-gray-950">
      <div className="relative w-full md:w-1/2 bg-gradient-to-br from-primary-700 via-primary-800 to-primary-950 dark:from-primary-800 dark:via-primary-900 dark:to-gray-950 flex flex-col justify-center items-center p-8 md:p-16 overflow-hidden">
        <div className="absolute top-0 left-0 w-72 h-72 bg-primary-500/30 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary-400/20 rounded-full blur-3xl translate-x-1/3 translate-y-1/3" />

        <div className="relative max-w-md w-full animate-fade-in-up">
          <div className="flex items-center gap-3 mb-8">
            <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20">
              <ShieldCheck className="h-6 w-6 text-white" />
            </div>
            <span className="text-white/90 text-sm font-medium tracking-wide uppercase">
              Autenticação de Dois Fatores
            </span>
          </div>

          <img
            className="mx-auto h-auto w-[180px] max-w-full sm:w-[220px]"
            src="/logo_branco.png"
            alt={config.name}
          />
          <h1 className="mt-8 text-3xl font-bold text-white text-center md:text-left tracking-tight">
            Verificação de Segurança
          </h1>
          <p className="mt-4 text-primary-100/80 text-lg text-center md:text-left leading-relaxed">
            Digite o código de 6 dígitos exibido no seu aplicativo autenticador para continuar.
          </p>
        </div>
      </div>

      <div className="w-full md:w-1/2 flex items-center justify-center p-8 md:p-16 bg-gradient-to-br from-white to-primary-50/30 dark:from-gray-900 dark:to-primary-950/10">
        <div className="max-w-md w-full space-y-8 glass-card rounded-2xl p-8 md:p-10 animate-scale-in">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white text-center tracking-tight">
              Código de Verificação
            </h2>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 text-center">
              Abra o Google Authenticator ou similar e digite o código abaixo
            </p>
          </div>

          <Form onSubmit={onSubmit} className="mt-8 space-y-6">
            <div className="flex justify-center gap-2 sm:gap-3" onPaste={handlePaste}>
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={el => {
                    inputsRef.current[index] = el;
                  }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={e => handleChange(index, e.target.value)}
                  onKeyDown={e => handleKeyDown(index, e)}
                  className="w-12 h-14 sm:w-14 sm:h-16 text-center text-2xl font-bold rounded-lg border-2 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500/40 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  style={{
                    borderColor: error
                      ? 'rgb(239 68 68)'
                      : digit
                        ? 'rgb(99 102 241)'
                        : 'rgb(209 213 219)',
                  }}
                />
              ))}
            </div>

            {error && (
              <p className="text-center text-sm text-red-600 dark:text-red-400">{error}</p>
            )}

            <Button type="submit" isLoading={isLoading} fullWidth size="lg">
              Confirmar
            </Button>

            <button
              type="button"
              onClick={handleBack}
              className="flex items-center justify-center w-full text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar para o login
            </button>
          </Form>
        </div>
      </div>
    </div>
  );
}

export default TwoFactorVerify;
